    <p-toast></p-toast>
    <app-page-shell>
      <app-page-hero eyebrow="HR Leave" title="Leave and Absence" subtitle="Manage leave requests, approval queues, balances, calendars, holidays, and operational assignment conflicts.">
        <div actions>
          <button pButton type="button" icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="load()" [disabled]="loading"></button>
        </div>
      </app-page-hero>

      @if (loading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
      }

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-hr-list-tabs
        [tabs]="[
          { key: 'requests', label: 'Leave Requests', icon: 'fa-solid fa-calendar-plus', badge: requests.length },
          { key: 'approvals', label: 'Approval Queue', icon: 'fa-solid fa-user-check', badge: pendingApprovals.length },
          { key: 'calendar', label: 'Team Calendar', icon: 'fa-solid fa-calendar-days', badge: calendarEntries.length },
          { key: 'balances', label: 'Leave Balances', icon: 'fa-solid fa-scale-balanced', badge: balances.length }
        ]"
        [(activeTab)]="activeListTab"
      ></app-hr-list-tabs>

      <section class="leave-top-grid">
        <app-hr-action-dialog [visible]="dialogVisible('request')" (visibleChange)="setDialogVisible('request', $event)" title="Submit Leave Request" eyebrow="Leave and Absence" subtitle="Calculate working days, validate balances, and identify assignment conflicts before submission." icon="fa-solid fa-calendar-plus" [contextTitle]="requestForm.employeeId ? 'Employee ' + requestForm.employeeId : 'New leave request'" [contextSubtitle]="requestForm.startDate && requestForm.endDate ? requestForm.startDate + ' to ' + requestForm.endDate : 'Date range required'">
        <app-page-form-section icon="fa-solid fa-calendar-plus" title="Submit Leave Request" subtitle="Requests calculate working days, check balances, detect overlaps, and warn about logistics assignment conflicts.">
          <div class="form-grid">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="leaveEmployeeId" [options]="employeeOptions" optionLabel="label" optionValue="value" [(ngModel)]="requestForm.employeeId" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="leaveEmployeeId">Employee</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-list"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="leaveType" [options]="leaveTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="requestForm.leaveTypeId" styleClass="w-full"></p-select>
                <label for="leaveType">Leave type</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="leaveStart" pInputText type="date" [(ngModel)]="requestForm.startDate" />
                <label for="leaveStart">Start date</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-check"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="leaveEnd" pInputText type="date" [(ngModel)]="requestForm.endDate" />
                <label for="leaveEnd">End date</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-triangle-exclamation"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="emergencyLeave" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="requestForm.emergencyLeave" styleClass="w-full"></p-select>
                <label for="emergencyLeave">Emergency leave</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup class="wide">
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="leaveReason" pTextarea rows="3" [(ngModel)]="requestForm.reason"></textarea>
                <label for="leaveReason">Reason</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="form-actions">
            <button pButton type="button" icon="fa-solid fa-paper-plane" label="Submit Request" (click)="submitLeave()" [disabled]="!canRequestLeave || !requestForm.employeeId || !requestForm.leaveTypeId || !requestForm.startDate || !requestForm.endDate"></button>
            <button pButton type="button" icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetRequest()"></button>
          </div>
        </app-page-form-section>
        </app-hr-action-dialog>

        <app-page-data-panel [hidden]="activeListTab !== 'approvals'" title="Manager Approval Queue" subtitle="Pending leave requests include assignment conflict warnings and payroll input preview." [resultText]="pendingApprovals.length + ' pending'">
          <p-table [value]="pendingApprovals" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '880px' }">
            <ng-template pTemplate="header">
              <tr><th>Employee</th><th>Leave</th><th>Dates</th><th>Days</th><th>Conflict</th><th style="width: 8rem">Decision</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-request>
              <tr>
                <td><strong>{{ request.employeeName || '-' }}</strong><br><small>{{ request.employeeNumber || request.employeeId }}</small></td>
                <td><strong>{{ request.leaveName || format(request.leaveCode) }}</strong><br><small>{{ format(request.requestStatus) }}</small></td>
                <td>{{ request.startDate | date:'mediumDate' }}<br><small>to {{ request.endDate | date:'mediumDate' }}</small></td>
                <td>{{ request.requestedDays ?? 0 }}</td>
                <td>
                  <p-tag [value]="request.assignmentConflictWarning ? 'Conflict' : 'Clear'" [severity]="request.assignmentConflictWarning ? 'warn' : 'success'"></p-tag>
                  @if (request.conflictSummary) { <small>{{ request.conflictSummary }}</small> }
                </td>
                <td>
                  <div class="row-actions">
                    <button pButton type="button" icon="pi pi-check" [rounded]="true" [text]="true" severity="success" (click)="approve(request)" [disabled]="!canApproveLeave"></button>
                    <button pButton type="button" icon="pi pi-times" [rounded]="true" [text]="true" severity="danger" (click)="reject(request)" [disabled]="!canApproveLeave"></button>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="6">No leave approvals are pending.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>

      <section class="leave-data-grid">
        <app-page-data-panel [hidden]="activeListTab !== 'requests'" title="Leave Requests" subtitle="Filter requests by status, date range, and employee." [resultText]="requests.length + ' requests'">
          @if (canRequestLeave) {
            <div actions><button pButton type="button" icon="fa-solid fa-plus" label="New Request" (click)="openDialog('request')"></button></div>
          }
          <div class="filter-grid">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-signal"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="leaveStatusFilter" [options]="statusOptions" optionLabel="label" optionValue="value" [showClear]="true" [(ngModel)]="filters.status" styleClass="w-full"></p-select>
                <label for="leaveStatusFilter">Status</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="leaveEmployeeFilter" [options]="employeeOptions" optionLabel="label" optionValue="value" [(ngModel)]="filters.employeeId" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="leaveEmployeeFilter">Employee</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="leaveFromFilter" pInputText type="date" [(ngModel)]="filters.from" />
                <label for="leaveFromFilter">From</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="leaveToFilter" pInputText type="date" [(ngModel)]="filters.to" />
                <label for="leaveToFilter">To</label>
              </p-iftaLabel>
            </p-inputGroup>

            <button pButton type="button" icon="pi pi-filter" label="Apply" (click)="loadRequests()"></button>
            <button pButton type="button" icon="pi pi-filter-slash" label="Clear" severity="secondary" [outlined]="true" (click)="clearFilters()"></button>
          </div>

          <p-table [value]="requests" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1180px' }" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr><th>Employee</th><th>Leave</th><th>Status</th><th>Dates</th><th>Paid/Unpaid</th><th>Conflict</th><th>Submitted</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-request>
              <tr>
                <td><strong>{{ request.employeeName || '-' }}</strong><br><small>{{ request.employeeNumber || request.employeeId }}</small></td>
                <td><strong>{{ request.leaveName || format(request.leaveCode) }}</strong><br><small>{{ request.reason || '-' }}</small></td>
                <td><p-tag [value]="format(request.requestStatus)" [severity]="statusSeverity(request.requestStatus)"></p-tag></td>
                <td>{{ request.startDate | date:'mediumDate' }}<br><small>{{ request.endDate | date:'mediumDate' }} · {{ request.requestedDays ?? 0 }} days</small></td>
                <td>{{ request.paidDays ?? 0 }} paid<br><small>{{ request.unpaidDays ?? 0 }} unpaid</small></td>
                <td>
                  <p-tag [value]="request.assignmentConflictWarning ? 'Warning' : 'None'" [severity]="request.assignmentConflictWarning ? 'warn' : 'success'"></p-tag>
                </td>
                <td>{{ request.submittedAt | date:'short' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="7">No leave requests found.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel [hidden]="activeListTab !== 'calendar'" title="Team Leave Calendar" subtitle="Approved and pending absence windows for workforce planning." [resultText]="calendarEntries.length + ' entries'">
          @if (canManageLeave) {
            <div actions>
              <button pButton type="button" icon="fa-solid fa-calendar-week" label="Calendar Setup" (click)="openDialog('calendar')"></button>
              <button pButton type="button" icon="fa-solid fa-umbrella-beach" label="New Holiday" severity="secondary" outlined (click)="openDialog('holiday')"></button>
            </div>
          }
          <p-table [value]="calendarEntries" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '860px' }">
            <ng-template pTemplate="header">
              <tr><th>Employee</th><th>Worker</th><th>Leave</th><th>Status</th><th>Window</th><th>Days</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-entry>
              <tr>
                <td><strong>{{ entry.employeeName || '-' }}</strong><br><small>{{ entry.employeeNumber || entry.employeeId }}</small></td>
                <td>{{ format(entry.workerCategory) }}</td>
                <td><span class="color-dot" [style.background]="entry.colorCode || '#64748b'"></span>{{ entry.leaveName || format(entry.leaveCode) }}</td>
                <td><p-tag [value]="format(entry.requestStatus)" [severity]="statusSeverity(entry.requestStatus)"></p-tag></td>
                <td>{{ entry.startDate | date:'mediumDate' }}<br><small>{{ entry.endDate | date:'mediumDate' }}</small></td>
                <td>{{ entry.requestedDays ?? 0 }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="6">No leave calendar entries found.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>

      <section class="leave-data-grid">
        <app-page-data-panel [hidden]="activeListTab !== 'balances'" title="Leave Balances" subtitle="Period-aware paid and unpaid leave inputs for future payroll integration." [resultText]="balances.length + ' balances'">
          <div actions>
            <button pButton type="button" icon="fa-solid fa-plus" label="New Leave Type" (click)="openDialog('type')" [disabled]="!canManageLeave"></button>
            <button pButton type="button" icon="fa-solid fa-calculator" label="Recalculate" [outlined]="true" (click)="recalculateBalances()" [disabled]="!canManageLeave"></button>
          </div>
          <p-table [value]="balances" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1040px' }">
            <ng-template pTemplate="header">
              <tr><th>Employee</th><th>Leave Type</th><th>Period</th><th>Entitled</th><th>Accrued</th><th>Used</th><th>Pending</th><th>Available</th><th>Updated</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-balance>
              <tr>
                <td><strong>{{ balance.employeeName || '-' }}</strong><br><small>{{ balance.employeeNumber || balance.employeeId }}</small></td>
                <td>{{ balance.leaveName || format(balance.leaveCode) }}</td>
                <td>{{ balance.balancePeriodStart | date:'mediumDate' }}<br><small>{{ balance.balancePeriodEnd | date:'mediumDate' }}</small></td>
                <td>{{ balance.entitledDays ?? 0 }}</td>
                <td>{{ balance.accruedDays ?? 0 }}</td>
                <td>{{ balance.usedDays ?? 0 }}</td>
                <td>{{ balance.pendingDays ?? 0 }}</td>
                <td><strong>{{ balance.availableDays ?? 0 }}</strong></td>
                <td>{{ balance.recalculatedAt | date:'short' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="9">No leave balances found.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-hr-action-dialog [visible]="dialogVisible('type')" (visibleChange)="setDialogVisible('type', $event)" title="Leave Type Settings" eyebrow="Leave Configuration" subtitle="Configure entitlement, balance behavior, payroll treatment, and employee visibility." icon="fa-solid fa-sliders" [contextTitle]="typeForm.leaveName || 'New leave type'" [contextSubtitle]="typeForm.leaveCode || 'Code required'">
        <app-page-form-section icon="fa-solid fa-sliders" title="Leave Type Settings" subtitle="Configure tenant leave types and payroll-ready paid/unpaid behavior.">
          <div class="form-grid">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-code"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="leaveCode" pInputText [(ngModel)]="typeForm.leaveCode" />
                <label for="leaveCode">Leave code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-heading"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="leaveName" pInputText [(ngModel)]="typeForm.leaveName" />
                <label for="leaveName">Leave name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-days"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-inputNumber inputId="annualEntitlementDays" [(ngModel)]="typeForm.annualEntitlementDays" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full"></p-inputNumber>
                <label for="annualEntitlementDays">Annual entitlement</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-money-bill-wave"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="paidLeave" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="typeForm.paidLeave" styleClass="w-full"></p-select>
                <label for="paidLeave">Paid leave</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-scale-balanced"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="requiresBalance" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="typeForm.requiresBalance" styleClass="w-full"></p-select>
                <label for="requiresBalance">Requires balance</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-arrow-down"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="allowNegativeBalance" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="typeForm.allowNegativeBalance" styleClass="w-full"></p-select>
                <label for="allowNegativeBalance">Negative balance</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-triangle-exclamation"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="typeEmergency" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="typeForm.emergencyLeave" styleClass="w-full"></p-select>
                <label for="typeEmergency">Emergency</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-toggle-on"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="typeActive" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="typeForm.active" styleClass="w-full"></p-select>
                <label for="typeActive">Active</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-palette"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="colorCode" pInputText [(ngModel)]="typeForm.colorCode" />
                <label for="colorCode">Color code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup class="wide">
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="leaveDescription" pTextarea rows="3" [(ngModel)]="typeForm.description"></textarea>
                <label for="leaveDescription">Description</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="form-actions">
            <button pButton type="button" icon="pi pi-save" label="Save Leave Type" (click)="saveType()" [disabled]="!canManageLeave || !typeForm.leaveCode || !typeForm.leaveName"></button>
            <button pButton type="button" icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetType()"></button>
          </div>

          <div class="chip-list">
            @for (type of leaveTypes; track type.leaveTypeId || type.leaveCode) {
              <button pButton type="button" [label]="type.leaveName || type.leaveCode || 'Leave Type'" icon="pi pi-pencil" severity="secondary" [outlined]="true" (click)="editType(type)"></button>
            }
          </div>
        </app-page-form-section>
        </app-hr-action-dialog>
      </section>

      <section class="leave-data-grid">
        <app-hr-action-dialog [visible]="dialogVisible('calendar')" (visibleChange)="setDialogVisible('calendar', $event)" title="Calendar Settings" eyebrow="Leave Configuration" subtitle="Define work calendars used by leave-day calculations." icon="fa-solid fa-calendar-week" [contextTitle]="calendarForm.calendarName || 'New calendar'" [contextSubtitle]="calendarForm.calendarCode || 'Code required'">
        <app-page-form-section icon="fa-solid fa-calendar-week" title="Calendar Settings" subtitle="Define tenant work calendars used by leave day calculation.">
          <div class="form-grid">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-code"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="calendarCode" pInputText [(ngModel)]="calendarForm.calendarCode" />
                <label for="calendarCode">Calendar code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-heading"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="calendarName" pInputText [(ngModel)]="calendarForm.calendarName" />
                <label for="calendarName">Calendar name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-xmark"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="weekendDays" pInputText [(ngModel)]="calendarForm.weekendDays" />
                <label for="weekendDays">Weekend days</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-star"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="defaultCalendar" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="calendarForm.defaultCalendar" styleClass="w-full"></p-select>
                <label for="defaultCalendar">Default</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="form-actions">
            <button pButton type="button" icon="pi pi-save" label="Save Calendar" (click)="saveCalendar()" [disabled]="!canManageLeave || !calendarForm.calendarCode || !calendarForm.calendarName"></button>
            <button pButton type="button" icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetCalendar()"></button>
          </div>
          <div class="chip-list">
            @for (calendar of calendars; track calendar.calendarId) {
              <button pButton type="button" [label]="calendar.calendarName || calendar.calendarCode || 'Calendar'" [icon]="calendar.defaultCalendar ? 'fa-solid fa-star' : 'pi pi-pencil'" severity="secondary" [outlined]="true" (click)="editCalendar(calendar)"></button>
            }
          </div>
        </app-page-form-section>
        </app-hr-action-dialog>

        <app-hr-action-dialog [visible]="dialogVisible('holiday')" (visibleChange)="setDialogVisible('holiday', $event)" title="Holiday Settings" eyebrow="Leave Configuration" subtitle="Maintain paid and unpaid holidays excluded from working-day calculations." icon="fa-solid fa-umbrella-beach" [contextTitle]="holidayForm.holidayName || 'New holiday'" [contextSubtitle]="holidayForm.holidayDate || 'Date required'">
        <app-page-form-section icon="fa-solid fa-umbrella-beach" title="Holiday Settings" subtitle="Paid holidays are excluded from leave day calculations and marked for time/payroll preparation.">
          <div class="form-grid">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="holidayDate" pInputText type="date" [(ngModel)]="holidayForm.holidayDate" />
                <label for="holidayDate">Holiday date</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-heading"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="holidayName" pInputText [(ngModel)]="holidayForm.holidayName" />
                <label for="holidayName">Holiday name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-money-bill-wave"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="paidHoliday" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="holidayForm.paidHoliday" styleClass="w-full"></p-select>
                <label for="paidHoliday">Paid holiday</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-toggle-on"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="holidayActive" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="holidayForm.active" styleClass="w-full"></p-select>
                <label for="holidayActive">Active</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="form-actions">
            <button pButton type="button" icon="pi pi-save" label="Save Holiday" (click)="saveHoliday()" [disabled]="!canManageLeave || !holidayForm.holidayDate || !holidayForm.holidayName"></button>
            <button pButton type="button" icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetHoliday()"></button>
          </div>

          <p-table [value]="holidays" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '620px' }">
            <ng-template pTemplate="header">
              <tr><th>Date</th><th>Name</th><th>Paid</th><th>Status</th><th style="width: 4rem"></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-holiday>
              <tr>
                <td>{{ holiday.holidayDate | date:'mediumDate' }}</td>
                <td>{{ holiday.holidayName }}</td>
                <td>{{ holiday.paidHoliday ? 'Paid' : 'Unpaid' }}</td>
                <td><p-tag [value]="holiday.active ? 'Active' : 'Inactive'" [severity]="holiday.active ? 'success' : 'secondary'"></p-tag></td>
                <td><button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true" (click)="editHoliday(holiday)"></button></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="5">No holidays configured.</td></tr>
            </ng-template>
          </p-table>
        </app-page-form-section>
        </app-hr-action-dialog>
      </section>
    </app-page-shell>
